import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import ReservasPublicasCard from "../components/ReservasPublicasCard";
import SolicitudesAprobacion from "../components/SolicitudesAprobacion";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import NewAppointmentModal from "../components/NewAppointmentModal";
import OverlapConfirmDialog from "../components/OverlapConfirmDialog";
import OccupancyPanel from "../components/OccupancyPanel";
import CobroModal from "../components/CobroModal";
import AnticipoIndicador from "../components/AnticipoIndicador";
import AnticipoModal from "../components/AnticipoModal";
import AnticipoDesenlaceDialog from "../components/AnticipoDesenlaceDialog";
import { pideDesenlace } from "../lib/anticipos";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Layers, Pencil, Plus, Receipt, User2 } from "lucide-react";

const ROW_STEP_MIN = 30;

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function fmtISO(d) {
  return d.toISOString().slice(0, 10);
}

// Mon=0..Sun=6, igual a day_of_week de doctor_availability y a Python's date.weekday().
function weekdayOf(d) {
  return (d.getDay() + 6) % 7;
}

function timeToMinutes(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function floorToStep(mins, step) {
  return Math.floor(mins / step) * step;
}

const dayLabel = (d) => d.toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" });

const MAX_VISIBLE_PER_CELL = 3;

export default function Agenda() {
  const [view, setView] = useState("week"); // "day" | "week"
  const [cursor, setCursor] = useState(new Date());
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [appts, setAppts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  // Control financiero. `cobrando` guarda la CITA que se esta cobrando (o consultando).
  const [cobrando, setCobrando] = useState(null);
  const { user, puede } = useAuth();
  // 🔴 En sesion demo el boton NO se pinta. No se deja para que falle con el 403 generico
  //    de `_DEMO_ALLOWED_MUTATIONS`: eso es DT.71, un error que no explica nada.
  const esDemo = !!user?.is_demo_session;
  // CL.4 A2 -- anticipos. Registrar/aplicar exigen `cobros`; anular, retener y devolver,
  // `cobros_anular` (capacidades.MATRIZ). Sin la capacidad no se ofrece la puerta.
  const puedeAnticipos = !esDemo && puede("cobros");
  // Corrección 1: el desenlace LEE el anticipo (`GET /anticipos`, capacidad `cobros`) y lo
  // resuelve (`cobros_anular`): hacen falta las dos, o el diálogo choca con un 403.
  const puedeResolverAnticipo = !esDemo && puede("cobros") && puede("cobros_anular");
  const [anticipoDe, setAnticipoDe] = useState(null);     // cita cuya ventana de anticipo esta abierta
  const [desenlaceDe, setDesenlaceDe] = useState(null);   // id de cita: decidir retener/devolver
  const [expandedCells, setExpandedCells] = useState(() => new Set());
  const [overlapConfirm, setOverlapConfirm] = useState(null); // { id, status, detail } | null
  const [overlapSubmitting, setOverlapSubmitting] = useState(false);
  const [occupancyForId, setOccupancyForId] = useState(null);

  const toggleCell = (key) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    api.get("/doctors").then((d) => setDoctors(d.data.items));
  }, []);

  const load = async () => {
    const params = {};
    if (doctorId) params.doctor_id = doctorId;
    const { data } = await api.get("/appointments", { params });
    setAppts(data.items);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [doctorId]);

  const week = useMemo(() => {
    const s = startOfWeek(cursor);
    return [...Array(7)].map((_, i) => {
      const d = new Date(s);
      d.setDate(s.getDate() + i);
      return d;
    });
  }, [cursor]);

  const days = useMemo(() => (view === "week" ? week : [cursor]), [view, week, cursor]);

  // Filas = union de (a) franjas de doctor_availability de los doctores relevantes para los
  // días de la semana visibles, y (b) horas de las citas que realmente existen en esos días.
  // Rejilla de 30 min continua entre el mínimo y el máximo -- nunca una lista fija (DT.28/S5c).
  const rows = useMemo(() => {
    const relevantDoctors = doctorId ? doctors.filter((d) => d.id === doctorId) : doctors;
    const weekdaysInView = new Set(days.map(weekdayOf));
    const visibleDates = new Set(days.map(fmtISO));

    const minutesSet = new Set();

    relevantDoctors.forEach((d) => {
      (d.availability || []).forEach((slot) => {
        if (!weekdaysInView.has(slot.day_of_week)) return;
        const start = timeToMinutes(slot.start_time);
        const end = timeToMinutes(slot.end_time);
        for (let m = floorToStep(start, ROW_STEP_MIN); m < end; m += ROW_STEP_MIN) {
          minutesSet.add(m);
        }
      });
    });

    appts.forEach((a) => {
      if (!visibleDates.has(a.date)) return;
      minutesSet.add(floorToStep(timeToMinutes(a.time), ROW_STEP_MIN));
    });

    if (minutesSet.size === 0) return [];
    const sorted = [...minutesSet].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const full = [];
    for (let m = min; m <= max; m += ROW_STEP_MIN) full.push(m);
    return full;
  }, [doctors, doctorId, appts, days]);

  const apptsByCell = useMemo(() => {
    const map = {};
    appts.forEach((a) => {
      const bucket = floorToStep(timeToMinutes(a.time), ROW_STEP_MIN);
      const key = `${a.date}_${bucket}`;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [appts]);

  const goPrev = () => {
    const d = new Date(cursor);
    d.setDate(d.getDate() - (view === "day" ? 1 : 7));
    setCursor(d);
  };
  const goNext = () => {
    const d = new Date(cursor);
    d.setDate(d.getDate() + (view === "day" ? 1 : 7));
    setCursor(d);
  };

  const cycleStatus = async (a) => {
    const order = ["Pendiente", "Confirmado", "Atendido", "No asistió", "Cancelado"];
    const next = order[(order.indexOf(a.status) + 1) % order.length];
    try {
      await api.patch(`/appointments/${a.id}`, { status: next });
      toast.success(`Cita marcada como ${next}`);
      load();
      // CL.4 A2: cancelada / no asistio con un anticipo recibido sin aplicar -> se pregunta
      // que pasa con el. Se puede posponer: nada se resuelve solo (decision de Jesus, 2 Oct).
      // El clic en la celda ya abrió el panel de Ocupación: se CIERRA antes de preguntar,
      // para no apilar dos ventanas con doble velo. La decisión del anticipo es lo urgente;
      // la ocupación se puede volver a abrir con otro clic.
      if (pideDesenlace(a, next) && puedeResolverAnticipo) {
        setOccupancyForId(null);
        setDesenlaceDe(a.id);
      }
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.code === "APPOINTMENT_OVERLAP") {
        // Pasa sobre todo al "descancelar" una cita: el hueco que dejo libre ya lo ocupo otra.
        setOverlapConfirm({ id: a.id, status: next, detail: e.response.data.detail });
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar la cita");
      }
    }
  };

  const cancelOverlap = () => setOverlapConfirm(null);

  const confirmOverlap = async () => {
    if (!overlapConfirm) return;
    setOverlapSubmitting(true);
    try {
      await api.patch(`/appointments/${overlapConfirm.id}`, { status: overlapConfirm.status, allow_overlap: true });
      toast.success(`Cita marcada como ${overlapConfirm.status} (empalmada a propósito)`);
      setOverlapConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar la cita");
    } finally {
      setOverlapSubmitting(false);
    }
  };

  const openNew = () => {
    setEditingAppt(null);
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditingAppt(a);
  };
  const closeModal = () => {
    setOpen(false);
    setEditingAppt(null);
  };

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-7xl mx-auto w-full">
      <ReservasPublicasCard />
      <SolicitudesAprobacion key={user?.active_clinic_id} />
      <PageHeader
        title="Agenda inteligente"
        subtitle="Vista diaria y semanal con filtro por doctor."
        actions={
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm" data-testid="agenda-new-appt">
            <Plus className="w-4 h-4" /> Nueva cita
          </button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-200">
          <div className="inline-flex rounded-lg overflow-hidden border border-slate-200 text-sm">
            <button onClick={() => setView("day")} className={`px-3 py-1.5 ${view === "day" ? "bg-brand-deep text-white" : "bg-white text-slate-600"}`} data-testid="agenda-view-day">
              Día
            </button>
            <button onClick={() => setView("week")} className={`px-3 py-1.5 ${view === "week" ? "bg-brand-deep text-white" : "bg-white text-slate-600"}`} data-testid="agenda-view-week">
              Semana
            </button>
          </div>
          <div className="inline-flex items-center gap-1">
            <button onClick={goPrev} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="agenda-prev"><ChevronLeft className="w-4 h-4" /></button>
            <div className="text-sm font-medium px-2">
              {view === "day"
                ? cursor.toLocaleDateString("es-MX", { weekday: "long", day: "2-digit", month: "long" })
                : `${week[0].toLocaleDateString("es-MX", { day: "2-digit", month: "short" })} – ${week[6].toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}`}
            </div>
            <button onClick={goNext} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="agenda-next"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setCursor(new Date())} className="text-xs font-medium px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 ml-1" data-testid="agenda-today">
              Hoy
            </button>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2 text-sm">
            <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5" data-testid="agenda-filter-doctor">
              <option value="">Todos los doctores</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Grid */}
        {rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
            <CalendarDays className="w-8 h-8 text-slate-300" />
            Ningún doctor tiene horario este {view === "day" ? "día" : "rango de días"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid" style={{ gridTemplateColumns: `80px repeat(${days.length}, minmax(0,1fr))` }}>
                <div className="border-b border-r border-slate-200 bg-slate-50" />
                {days.map((d) => (
                  <div key={d.toISOString()} className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    {dayLabel(d)}
                  </div>
                ))}

                {rows.map((m) => (
                  <React.Fragment key={m}>
                    <div className="border-b border-r border-slate-100 text-[11px] text-slate-400 px-2 py-3 font-mono">
                      {minutesToHHMM(m)}
                    </div>
                    {days.map((d) => {
                      const cellKey = `${fmtISO(d)}_${m}`;
                      const cell = apptsByCell[cellKey] || [];
                      const expanded = expandedCells.has(cellKey);
                      const visible = expanded ? cell : cell.slice(0, MAX_VISIBLE_PER_CELL);
                      const hiddenCount = cell.length - visible.length;
                      return (
                        <div key={`${d.toISOString()}_${m}`} className="border-b border-slate-100 min-h-[64px] p-1.5 space-y-1 relative">
                          {visible.map((a) => (
                            <div
                              key={a.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => { setOccupancyForId(a.id); cycleStatus(a); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { setOccupancyForId(a.id); cycleStatus(a); } }}
                              className="w-full text-left rounded-lg px-2 py-1.5 text-[11px] border bg-white hover:shadow transition-shadow cursor-pointer"
                              style={{
                                borderColor: a.status === "No asistió" ? "#fecaca" : a.status === "Confirmado" ? "#bbf7d0" : a.status === "Cancelado" ? "#e2e8f0" : "#dbeafe",
                                background:
                                  a.status === "No asistió"
                                    ? "#fef2f2"
                                    : a.status === "Confirmado"
                                    ? "#ecfdf5"
                                    : a.status === "Atendido"
                                    ? "#f1f5f9"
                                    : a.status === "Cancelado"
                                    ? "#f8fafc"
                                    : "#eff6ff",
                                // Franja de color del doctor, para distinguir citas simultaneas de
                                // doctores distintos sin pisar el color de estado (arriba).
                                borderLeftWidth: "4px",
                                borderLeftColor: a.doctor_color || "#94a3b8",
                              }}
                              data-testid={`agenda-appt-${a.id}`}
                            >
                              <div className="flex items-start justify-between gap-1">
                                <div className="font-semibold text-slate-900 truncate flex items-center gap-1">
                                  {a.allow_overlap && (
                                    <Layers
                                      className="w-3 h-3 shrink-0 text-amber-500"
                                      title="Empalmada a propósito"
                                      data-testid={`agenda-appt-overlap-${a.id}`}
                                    />
                                  )}
                                  <span className="truncate">{a.patient_name}</span>
                                </div>
                                {/* 🔴 stopPropagation, igual que el lapiz: el clic de la celda
                                    CICLA EL ESTADO de la cita, y nadie debe marcar "Atendido"
                                    sin querer por intentar cobrar. */}
                                {!esDemo && a.status !== "Cancelado" && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setCobrando(a); }}
                                    className={`p-1 rounded hover:bg-white/70 ${a.cobro_id ? "text-emerald-600" : "text-slate-400 hover:text-brand-electric"}`}
                                    title={a.cobro_id ? `Cobro ${a.cobro_estado}` : "Cobrar"}
                                    data-testid={`agenda-appt-cobrar-${a.id}`}
                                  >
                                    <Receipt className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                                  className="shrink-0 text-slate-400 hover:text-brand-electric p-0.5 -mt-0.5 -mr-0.5"
                                  title="Editar horario"
                                  data-testid={`agenda-appt-edit-${a.id}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="text-slate-500 truncate">{a.service_name || "Sin servicio"} · {a.time}</div>
                              <div className="text-slate-600 font-medium truncate flex items-center gap-1">
                                <User2 className="w-3 h-3 shrink-0" style={{ color: a.doctor_color || "#94a3b8" }} />
                                {a.doctor_name || "Sin doctor asignado"}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <StatusPill status={a.status} />
                                <AnticipoIndicador cita={a} onAbrir={puedeAnticipos ? setAnticipoDe : undefined} />
                              </div>
                            </div>
                          ))}
                          {hiddenCount > 0 && (
                            <button
                              onClick={() => toggleCell(cellKey)}
                              className="w-full text-center rounded-lg py-1 text-[11px] font-medium text-brand-electric bg-blue-50 hover:bg-blue-100 border border-blue-100"
                              data-testid={`agenda-cell-more-${cellKey}`}
                            >
                              +{hiddenCount} más
                            </button>
                          )}
                          {expanded && cell.length > MAX_VISIBLE_PER_CELL && (
                            <button
                              onClick={() => toggleCell(cellKey)}
                              className="w-full text-center rounded-lg py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100 border border-slate-200"
                            >
                              Ver menos
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="px-5 py-3 border-t border-slate-200 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className="font-semibold mr-2">Leyenda:</span>
          <StatusPill status="Pendiente" />
          <StatusPill status="Confirmado" />
          <StatusPill status="Atendido" />
          <StatusPill status="No asistió" />
          <StatusPill status="Cancelado" />
          <span className="ml-auto text-slate-400 inline-flex items-center gap-1">
            Clic en una cita cambia su estado · el lápiz edita horario · <Layers className="w-3 h-3 text-amber-500" /> empalmada a propósito
          </span>
        </div>
      </div>

      {(open || editingAppt) && (
        <NewAppointmentModal
          open={open || !!editingAppt}
          onClose={closeModal}
          onCreated={() => { setEditingAppt(null); load(); }}
          editingAppointment={editingAppt}
        />
      )}

      <OccupancyPanel
        open={!!occupancyForId}
        onClose={() => setOccupancyForId(null)}
        appointments={appts}
        selectedId={occupancyForId}
        onCobrar={esDemo ? undefined : (a) => { setOccupancyForId(null); setCobrando(a); }}
        onAnticipo={puedeAnticipos ? (a) => { setOccupancyForId(null); setAnticipoDe(a); } : undefined}
      />

      {anticipoDe && (
        <AnticipoModal
          open={!!anticipoDe}
          cita={appts.find((x) => x.id === anticipoDe.id) || anticipoDe}
          onClose={() => setAnticipoDe(null)}
          onCambio={load}
          onResolver={(c) => { setAnticipoDe(null); setDesenlaceDe(c.id); }}
          puedeRegistrar={puedeAnticipos}
          puedeAnular={puedeResolverAnticipo}
          esDemo={esDemo}
        />
      )}

      <AnticipoDesenlaceDialog
        open={!!desenlaceDe}
        citaId={desenlaceDe}
        onPosponer={() => setDesenlaceDe(null)}
        onHecho={() => { setDesenlaceDe(null); load(); }}
      />

      {cobrando && (
        <CobroModal
          open={!!cobrando}
          cita={cobrando}
          onClose={() => setCobrando(null)}
          onCambio={load}
        />
      )}

      {/* Despues del panel de ocupacion a proposito: si el clic tambien dispara un 409, esta
          decision es mas urgente y debe quedar visualmente encima. */}
      <OverlapConfirmDialog
        open={!!overlapConfirm}
        detail={overlapConfirm?.detail}
        confirming={overlapSubmitting}
        onCancel={cancelOverlap}
        onConfirm={confirmOverlap}
        cancelLabel="Cancelar"
      />
    </div>
  );
}
